<script>
    import { createEventDispatcher, onMount, onDestroy } from 'svelte';
    import { slide } from 'svelte/transition';
    import { get } from 'svelte/store';
    import { sectionHeaders } from '../editors/spec/blocks/blockDefinitions.js';
    import { specStore } from '../../stores/editorsStore.js';
    import {
        sectionSubsections,
        getAllSubsections,
        regenerativeSections,
        buildSectionGroups,
        getDefaultSelectedSections,
        getSectionsInDocument,
        syncWithDocument as syncWithDocumentUtil,
        getAllAvailableSectionIds,
        getParentSection,
        getSectionById,
        isRegenerativeSection
    } from './regenerateDropdownUtils.js';

    const dispatch = createEventDispatcher();

    export let isRegenerating = false;

    let showDropdown = false;
    let dropdownRef;
    let buttonRef;

    // Helper to get all subsections as flat array
    const allSubsections = getAllSubsections();

    const sectionGroups = buildSectionGroups();

    let selectedSections = getDefaultSelectedSections();

    // Sync selected sections with what exists in the document
    function syncWithDocument(htmlContent) {
        selectedSections = syncWithDocumentUtil(htmlContent, getDefaultSelectedSections);
    }

    // Sync with document when dropdown is opened (not on every content change to avoid cursor jumps)
    function syncOnDropdownOpen() {
        const storeValue = get(specStore);
        if (storeValue.content) {
            syncWithDocument(storeValue.content);
        }
    }
    
    function toggleDropdown(e) {
        e.stopPropagation();
        const wasOpen = showDropdown;
        showDropdown = !showDropdown;

        // Sync selections with document when opening the dropdown
        if (!wasOpen && showDropdown) {
            syncOnDropdownOpen();
        }
    }
    
    function handleClickOutside(event) {
        if (dropdownRef && !dropdownRef.contains(event.target) && 
            buttonRef && !buttonRef.contains(event.target)) {
            showDropdown = false;
        }
    }
    
    function handleKeydown(e) {
        if (e.key === 'Escape') {
            showDropdown = false;
        }
    }
    
    function toggleSection(sectionId) {
        if (selectedSections.has(sectionId)) {
            selectedSections.delete(sectionId);
        } else {
            selectedSections.add(sectionId);
        }
        selectedSections = new Set(selectedSections); // Trigger reactivity
    }
    
    function selectAll() {
        selectedSections = new Set(getAllAvailableSectionIds());
    }
    
    function clearAll() {
        selectedSections = new Set();
    }
    
    function handleRegenerate() {
        showDropdown = false;
        
        // Get parent sections for outline creation (deduplicated)
        const parentSections = new Set();
        for (const sectionId of selectedSections) {
            const parentId = getParentSection(sectionId);
            parentSections.add(parentId || sectionId);
        }
        
        // Get section details for parent sections
        const sectionsToProcess = sectionHeaders.filter(s => parentSections.has(s.id));
        
        // Build regenerative sections with proper context mapping
        const regenerativeSectionsToProcess = [];
        console.log('[DEBUG-DROPDOWN] Selected sections:', Array.from(selectedSections));
        console.log('[DEBUG-DROPDOWN] Regenerative sections set:', Array.from(regenerativeSections));

        for (const sectionId of selectedSections) {
            const subsection = allSubsections.find(sub => sub.id === sectionId);
            if (subsection && subsection.regenerative) {
                // This is a subsection with regenerative content
                const parentId = getParentSection(sectionId);
                const parentSection = sectionHeaders.find(s => s.id === parentId);
                console.log(`[DEBUG-DROPDOWN] Adding subsection: ${sectionId} -> context: ${subsection.context}`);
                regenerativeSectionsToProcess.push({
                    id: parentId,
                    title: parentSection.title,
                    context: subsection.context
                });
            } else if (regenerativeSections.has(sectionId)) {
                // This is a standard regenerative section
                const section = sectionHeaders.find(s => s.id === sectionId);
                console.log(`[DEBUG-DROPDOWN] Adding standard regenerative section: ${sectionId} -> context: ${sectionId}`);
                regenerativeSectionsToProcess.push({
                    id: sectionId,
                    title: section.title,
                    context: sectionId
                });
            }
        }

        console.log('[DEBUG-DROPDOWN] Final regenerativeSectionsToProcess:', regenerativeSectionsToProcess);
        
        // Non-regenerative sections are those that don't have regenerative content
        const outlineSectionsToProcess = sectionsToProcess.filter(s => 
            !regenerativeSections.has(s.id) && 
            !sectionSubsections[s.id]?.some(sub => sub.regenerative)
        );
        
        dispatch('regenerate', {
            selectedSections: Array.from(parentSections),
            regenerativeSections: regenerativeSectionsToProcess,
            outlineSections: outlineSectionsToProcess,
            allSectionDetails: sectionsToProcess
        });
    }

    onMount(() => {
        document.addEventListener('click', handleClickOutside);
        document.addEventListener('keydown', handleKeydown);
    });
    
    onDestroy(() => {
        document.removeEventListener('click', handleClickOutside);
        document.removeEventListener('keydown', handleKeydown);
    });
</script>

<div class="regenerate-dropdown-container">
    <div class="button-group" bind:this={buttonRef}>
        <button
            class="toolbar-button spec-button main-button"
            on:click={handleRegenerate}
            disabled={isRegenerating || selectedSections.size === 0}
            title={selectedSections.size === 0 ? "Select sections to regenerate" : "Create outline and regenerate content"}
            data-testid="regenerate-spec-button"
        >
            {#if isRegenerating}
                <i class="fas fa-spinner fa-spin"></i>
            {:else}
                <i class="fas fa-sync-alt"></i>
            {/if}
            <span class="button-text">{isRegenerating ? 'Generating...' : 'Generate Spec'}</span>
        </button>
        <button
            class="toolbar-button spec-button dropdown-trigger"
            on:click={toggleDropdown}
            disabled={isRegenerating}
            title="Select sections"
            data-testid="regenerate-dropdown-trigger"
            aria-expanded={showDropdown}
        >
            <i class="fas fa-chevron-down"></i>
        </button>
    </div>
    
    {#if showDropdown}
        <div 
            class="sections-dropdown" 
            bind:this={dropdownRef}
            transition:slide|local={{ duration: 200 }}
        >
            <div class="dropdown-header">
                <h4>Select Sections to Include</h4>
                <div class="header-actions">
                    <button class="link-button" on:click={selectAll}>Select All</button>
                    <span class="separator">|</span>
                    <button class="link-button" on:click={clearAll}>Clear All</button>
                </div>
            </div>
            
            <div class="dropdown-content">
                {#each Object.entries(sectionGroups) as [groupName, sectionIds]}
                    <div class="section-group">
                        <h5 class="group-title">{groupName}</h5>
                        {#each sectionIds as sectionId}
                            {@const section = getSectionById(sectionId)}
                            {#if section}
                                <label class="section-item">
                                    <input
                                        type="checkbox"
                                        checked={selectedSections.has(sectionId)}
                                        on:change={() => toggleSection(sectionId)}
                                    />
                                    <span class="section-label">
                                        {section.title}
                                        {#if isRegenerativeSection(sectionId)}
                                            <span class="regenerative-badge" title="Supports AI-generated content">
                                                <i class="fas fa-magic"></i>
                                            </span>
                                        {/if}
                                    </span>
                                </label>
                            {/if}
                        {/each}
                    </div>
                {/each}
            </div>
            
            <div class="dropdown-footer">
                <div class="footer-info">
                    <i class="fas fa-info-circle"></i>
                    <span>Sections with <i class="fas fa-magic"></i> include regenerative content. These sections will be regenerated when the generate button is clicked again.</span>
                </div>
                <button 
                    class="apply-button"
                    on:click={handleRegenerate}
                    disabled={selectedSections.size === 0}
                >
                    <div class="apply-button-content">
                        <span class="apply-text">Apply</span>
                        <span class="selected-count">{selectedSections.size} selected</span>
                    </div>
                </button>
            </div>
        </div>
    {/if}
</div>

<style>
.regenerate-dropdown-container {
    position: relative;
    display: inline-flex;
}

.button-group {
    display: flex;
    overflow: hidden;
    border-radius: var(--border-radius-2xl);
}

.main-button {
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
    border-right: 1px solid rgba(255, 255, 255, 0.2);
}

.dropdown-trigger {
    min-width: auto;
    padding: 0 var(--spacing-2);
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
}

.dropdown-trigger i {
    transition: transform 0.2s ease;
}

.dropdown-trigger[aria-expanded="true"] i {
    transform: rotate(180deg);
}

.sections-dropdown {
    position: absolute;
    top: calc(100% + var(--spacing-2));
    right: 0;
    z-index: var(--z-index-dropdown);
    width: 320px;
    overflow: hidden;
    background-color: var(--color-surface-content-spec);
    border-radius: var(--border-radius-2xl);
    box-shadow: var(--shadow-dropdown);
}

.dropdown-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: var(--spacing-3) var(--spacing-4);
    background-color: var(--color-surface-header-spec);
    border-bottom: 1px solid var(--color-border-light);
}

.dropdown-header h4 {
    margin: 0;
    font-size: var(--font-size-sm);
    font-weight: 600;
    color: var(--color-text-primary);
}

.header-actions {
    display: flex;
    align-items: center;
    gap: var(--spacing-2);
    font-size: var(--font-size-xs);
}

.link-button {
    padding: 0;
    font-size: inherit;
    text-decoration: underline;
    color: var(--color-primary);
    background: none;
    border: none;
    cursor: pointer;
}

.link-button:hover {
    color: var(--color-primary-hover);
}

.separator {
    color: var(--color-text-tertiary);
}

.dropdown-content {
    max-height: 400px;
    padding: var(--spacing-2);
    overflow-y: auto;
}

.section-group {
    margin-bottom: var(--spacing-3);
}

.section-group:last-child {
    margin-bottom: 0;
}

.group-title {
    padding: var(--spacing-1) var(--spacing-2);
    margin: 0 0 var(--spacing-2) 0;
    font-size: var(--font-size-xs);
    font-weight: 600;
    text-transform: uppercase;
    color: var(--color-text-secondary);
    letter-spacing: 0.05em;
}

.section-item {
    display: flex;
    align-items: center;
    padding: var(--spacing-2) var(--spacing-2);
    margin-bottom: var(--spacing-1);
    border-radius: var(--border-radius-md);
    transition: background-color 0.2s ease;
    cursor: pointer;
}

.section-item:hover {
    background-color: var(--color-bg-hover-light);
}

.section-item input[type="checkbox"] {
    margin-right: var(--spacing-2);
    cursor: pointer;
}

.section-label {
    display: flex;
    flex: 1;
    align-items: center;
    font-size: var(--font-size-sm);
    color: var(--color-text-primary);
    gap: var(--spacing-2);
}

.regenerative-badge {
    display: inline-flex;
    align-items: center;
    padding: 2px 6px;
    font-size: var(--font-size-xs);
    color: var(--color-spec-600);
    background-color: var(--color-spec-100);
    border-radius: var(--border-radius-sm);
}

.regenerative-badge i {
    font-size: 10px;
}

.dropdown-footer {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    padding: var(--spacing-3) var(--spacing-4);
    background-color: var(--color-surface-header-spec);
    border-top: 1px solid var(--color-border-light);
    gap: var(--spacing-1);
}

.footer-info {
    display: flex;
    align-items: center;
    gap: var(--spacing-2);
    font-size: var(--font-size-xs);
    color: var(--color-text-secondary);
}

.footer-info i {
    color: var(--color-spec-600);
}

.apply-button {
    min-width: auto;
    padding: var(--spacing-2) var(--spacing-3);
    font-size: var(--font-size-sm);
    font-weight: 500;
    text-align: center;
    color: var(--color-text-secondary);
    background-color: var(--color-btn-spec);
    border: none;
    border-radius: var(--border-radius-xl);
    transition: background-color 0.2s var(--ease-in-out);
    cursor: pointer;
}

.apply-button-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 2px;
}

.apply-text {
    font-size: var(--font-size-sm);
    font-weight: 500;
    line-height: 1;
}

.selected-count {
    font-size: 10px;
    font-weight: 400;
    line-height: 1;
    white-space: nowrap;
    opacity: 0.75;
}

.apply-button:hover:not(:disabled) {
    background-color: var(--color-btn-spec-hover);
}

.apply-button:disabled {
    opacity: 0.7;
    cursor: not-allowed;
}

/* Text color adjustments to match toolbar buttons */
:global([data-theme="light"]) .apply-button {
    color: var(--color-text-secondary);
}

:global([data-theme="dark"]) .apply-button {
    color: var(--color-text-primary);
}

/* Scrollbar styling */
.dropdown-content::-webkit-scrollbar {
    width: 6px;
}

.dropdown-content::-webkit-scrollbar-track {
    background: var(--color-bg-secondary);
    border-radius: 3px;
}

.dropdown-content::-webkit-scrollbar-thumb {
    background: var(--color-border-medium);
    border-radius: 3px;
}

.dropdown-content::-webkit-scrollbar-thumb:hover {
    background: var(--color-border-dark);
}

/* Dark mode adjustments */
:global([data-theme="dark"]) .sections-dropdown {
    background-color: var(--color-surface-content-spec);
}

:global([data-theme="dark"]) .dropdown-header,
:global([data-theme="dark"]) .dropdown-footer {
    background-color: var(--color-surface-header-spec);
}

:global([data-theme="dark"]) .regenerative-badge {
    color: var(--color-spec-400);
    background-color: rgba(255, 146, 195, 0.2);
}

/* Responsive text hiding - hide text earlier than other spec buttons */
@media (width <= 1700px) {
    .main-button .button-text {
        display: none;
    }
}
</style>